import { useCallback, useMemo, useState } from 'react'
import { Linking, Platform, ScrollView, StyleSheet, Text, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Clock, LogOut, Mail, ShieldAlert } from '@/components/ui/icons'

import { BRAND, FONTS } from '../../lib/brand'
import { RADIUS, SHADOWS, SPACING, useSemanticColors } from '../../lib/theme/tokens'
import { useTheme } from '../../lib/hooks/use-theme'
import { formatBookingDateTime } from '../../lib/utils/dates'
import type { UserProfile } from '../../lib/types/auth.types'
import { Button } from './Button'

type AccountRestrictedScreenProps = {
  profile: UserProfile
  onSignOut: () => Promise<unknown> | unknown
}

function formatRestrictionDate(value: string | null) {
  if (!value) return null

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null

  return formatBookingDateTime(date, 'es')
}

export function AccountRestrictedScreen({ profile, onSignOut }: AccountRestrictedScreenProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const [signingOut, setSigningOut] = useState(false)

  const isArchived = Boolean(profile.deletedAt)
  const formattedUntil = formatRestrictionDate(profile.suspendedUntil)
  const reason = (isArchived ? profile.deletionReason : profile.suspendedReason)?.trim() || null

  const title = isArchived
    ? t('account_status.archived_title')
    : formattedUntil
      ? t('account_status.suspended_until_title')
      : t('account_status.suspended_title')

  const body = isArchived
    ? t('account_status.archived_body')
    : formattedUntil
      ? t('account_status.suspended_until_body')
      : t('account_status.suspended_body')

  const supportEmail = BRAND.contact.email ?? 'team@tago.rent'
  const supportUrl = useMemo(() => {
    const subject = encodeURIComponent(t('account_status.support_subject'))
    const message = encodeURIComponent(
      t('account_status.support_body', { email: profile.email || profile.id }),
    )
    return `mailto:${supportEmail}?subject=${subject}&body=${message}`
  }, [profile.email, profile.id, supportEmail, t])

  const handleSupport = useCallback(() => {
    void Linking.openURL(supportUrl)
  }, [supportUrl])

  const handleSignOut = useCallback(async () => {
    setSigningOut(true)
    try {
      await onSignOut()
    } finally {
      setSigningOut(false)
    }
  }, [onSignOut])

  return (
    <SafeAreaView
      edges={['top', 'bottom']}
      style={[styles.safeArea, { backgroundColor: colors.background }]}
      testID="account-restricted.screen"
    >
      <ScrollView
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={Platform.OS === 'web'}
      >
        <View
          style={[
            styles.panel,
            {
              backgroundColor: colors.surface,
              borderColor: colors.border,
            },
            isDark ? SHADOWS.none : SHADOWS.sm,
          ]}
        >
          <View
            style={[
              styles.iconShell,
              {
                backgroundColor: isDark ? 'rgba(255,106,82,0.12)' : 'rgba(240,66,45,0.08)',
                borderColor: isDark ? 'rgba(255,106,82,0.28)' : 'rgba(240,66,45,0.18)',
              },
            ]}
          >
            {isArchived ? (
              <ShieldAlert size={26} color={colors.primary} strokeWidth={1.9} />
            ) : (
              <AlertTriangle size={26} color={colors.primary} strokeWidth={1.9} />
            )}
          </View>

          <View style={styles.copy}>
            <Text style={[styles.eyebrow, { color: colors.textSecondary }]}>
              {t('account_status.eyebrow')}
            </Text>
            <Text style={[styles.title, { color: colors.textPrimary }]}>{title}</Text>
            <Text style={[styles.body, { color: colors.textSecondary }]}>{body}</Text>
          </View>

          {reason || formattedUntil ? (
            <View
              style={[
                styles.details,
                {
                  backgroundColor: colors.surfaceSubtle,
                  borderColor: colors.border,
                },
              ]}
            >
              {reason ? (
                <View style={styles.detailRow} testID="account-restricted.reason">
                  <View style={styles.detailIcon}>
                    <ShieldAlert size={18} color={colors.textSecondary} strokeWidth={1.8} />
                  </View>
                  <View style={styles.detailCopy}>
                    <Text style={[styles.detailLabel, { color: colors.textSecondary }]}>
                      {t('account_status.reason_label')}
                    </Text>
                    <Text style={[styles.detailValue, { color: colors.textPrimary }]}>
                      {reason}
                    </Text>
                  </View>
                </View>
              ) : null}

              {formattedUntil ? (
                <View style={styles.detailRow} testID="account-restricted.until">
                  <View style={styles.detailIcon}>
                    <Clock size={18} color={colors.textSecondary} strokeWidth={1.8} />
                  </View>
                  <View style={styles.detailCopy}>
                    <Text style={[styles.detailLabel, { color: colors.textSecondary }]}>
                      {t('account_status.until_label')}
                    </Text>
                    <Text style={[styles.detailValue, { color: colors.textPrimary }]}>
                      {formattedUntil}
                    </Text>
                  </View>
                </View>
              ) : null}
            </View>
          ) : null}

          <View style={styles.actions}>
            <Button
              title={t('account_status.support')}
              icon={<Mail size={18} />}
              onPress={handleSupport}
              size="lg"
              testID="account-restricted.support-button"
            />
            <Button
              title={t('account_status.sign_out')}
              icon={<LogOut size={18} />}
              onPress={handleSignOut}
              loading={signingOut}
              disabled={signingOut}
              variant="secondary"
              size="lg"
              testID="account-restricted.signout-button"
            />
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  content: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: SPACING.lg,
    paddingVertical: SPACING['2xl'],
  },
  panel: {
    width: '100%',
    maxWidth: 560,
    borderWidth: 1,
    borderRadius: RADIUS.xl,
    padding: SPACING.xl,
    gap: SPACING.lg,
  },
  iconShell: {
    width: 58,
    height: 58,
    borderRadius: RADIUS.full,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  copy: {
    gap: SPACING.sm,
  },
  eyebrow: {
    fontFamily: FONTS.bodyBold,
    fontSize: 12,
    lineHeight: 16,
    textTransform: 'uppercase',
  },
  title: {
    fontFamily: FONTS.bodyBold,
    fontSize: 30,
    lineHeight: 36,
  },
  body: {
    fontFamily: FONTS.body,
    fontSize: 16,
    lineHeight: 24,
  },
  details: {
    borderWidth: 1,
    borderRadius: RADIUS.lg,
    padding: SPACING.md,
    gap: SPACING.md,
  },
  detailRow: {
    flexDirection: 'row',
    gap: SPACING.md,
  },
  detailIcon: {
    width: 24,
    alignItems: 'center',
    paddingTop: 2,
  },
  detailCopy: {
    flex: 1,
    gap: 2,
  },
  detailLabel: {
    fontFamily: FONTS.bodyBold,
    fontSize: 12,
    lineHeight: 16,
  },
  detailValue: {
    fontFamily: FONTS.body,
    fontSize: 15,
    lineHeight: 22,
  },
  actions: {
    gap: SPACING.sm,
  },
})
